import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardBody, Spinner, Badge } from "../components/ui";
import { FaUsers, FaCheckCircle, FaTimesCircle } from "react-icons/fa";

export default function AcceptInvite() {
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isLoggedIn = !!isAuthenticated;
  const invitePath = `/invite/${inviteToken}`;
  const inviteNext = encodeURIComponent(invitePath);

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    apiGet(`/api/settings/team/invite/${inviteToken}`)
      .then((res) => {
        if (res?.success) setInvite(res.invite);
        else setError(res?.message || "Invalid invite");
      })
      .catch(() => setError("Failed to load invite details"))
      .finally(() => setLoading(false));
  }, [inviteToken]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await apiPost(`/api/settings/team/invite/${inviteToken}/accept`);
      if (res?.success) {
        const workspaceId = res.activeWorkspace?.id || res.workspaceId || res.clientId;
        if (workspaceId) localStorage.setItem("activeWorkspaceId", workspaceId);
        setAccepted(true);
      } else {
        setError(res?.message || "Failed to accept invite");
      }
    } catch (err) {
      setError(err?.message || "Failed to accept invite");
    } finally {
      setAccepting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-bg">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-bg p-4">
      <Card className="max-w-md w-full text-center">
        <CardBody className="py-10 px-8">
          {error && !accepted ? (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: "rgba(239,68,68,0.1)" }}>
                <FaTimesCircle size={32} className="text-red-500" />
              </div>
              <h1 className="text-xl font-bold text-ink-primary mb-2">Invite Error</h1>
              <p className="text-ink-muted mb-6">{error}</p>
              <button className="btn-secondary" onClick={() => navigate("/login")}>
                Go to Login
              </button>
            </>
          ) : accepted ? (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: "rgba(0,200,151,0.1)" }}>
                <FaCheckCircle size={32} className="text-brand" />
              </div>
              <h1 className="text-xl font-bold text-ink-primary mb-2">You're In!</h1>
              <p className="text-ink-muted mb-6">
                You've joined the workspace. We'll switch you there now.
              </p>
              <button className="btn-primary w-full" onClick={() => { window.location.href = "/dashboard"; }}>
                Go to Workspace
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: "rgba(0,200,151,0.1)" }}>
                <FaUsers size={28} className="text-brand" />
              </div>
              <h1 className="text-xl font-bold text-ink-primary mb-2">Team Invite</h1>
              <p className="text-ink-muted mb-6">
                <strong>{invite.ownerName}</strong> invited you to join their Walytic workspace.
              </p>

              <div className="space-y-3 mb-8 text-left">
                <div className="flex justify-between p-3 rounded-lg bg-surface-bg">
                  <span className="text-sm text-ink-muted">Role</span>
                  <Badge variant="brand" className="capitalize">{invite.role === "member" ? "User" : invite.role}</Badge>
                </div>
                <div className="flex justify-between p-3 rounded-lg bg-surface-bg">
                  <span className="text-sm text-ink-muted">Expires</span>
                  <span className="text-sm font-medium text-ink-primary">
                    {invite.expiresAt ? new Date(invite.expiresAt).toLocaleDateString() : "Never"}
                  </span>
                </div>
              </div>

              {isLoggedIn ? (
                <button
                  className="btn-primary w-full"
                  onClick={handleAccept}
                  disabled={accepting}
                >
                  {accepting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size="sm" /> Accepting...
                    </span>
                  ) : (
                    "Accept Invite"
                  )}
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-ink-muted">Sign in to accept this invite</p>
                  <button
                    className="btn-primary w-full"
                    onClick={() => navigate(`/login?next=${inviteNext}`)}
                  >
                    Log In
                  </button>
                  <button
                    className="btn-secondary w-full"
                    onClick={() => navigate(`/signup?next=${inviteNext}`)}
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
